<template>
  <div class="compare-view">
    <ftu-block
      dismiss-key="compare"
      icon="multiline_chart"
      title="This is the Compare tool"
      description="The Compare tool allows you to compare the sales histories of multiple verified cards on the same graph. Simply search the cards you want to compare and toggle between time frames to discover trends, inefficiencies, and more. You can also pick cards to compare right from their profiles and save comparison sets for future use."
    />

    <page-header title="Compare">
      <template #actions>
        <saved-options-dropdown
          label="Saved Comparisons"
          :options="comparisons"
          :save-disabled="!showSaveButton"
          @edit="editComparison"
          @delete="removeComparison"
          @select="loadComparison"
          @save="saveComparison"
        />
      </template>
    </page-header>

    <template>
      <div class="tabs">
        <tab :active="type === 'cards'" @click="type = 'cards'">
          Cards
        </tab>

        <tab :active="type === 'indexes'" @click="type = 'indexes'">
          Indexes
        </tab>
      </div>

      <div class="wrapper">
        <div v-if="!disabled" class="search-container">
          <card-search
            v-show="type === 'cards'"
            keep-open-on-select
            :exclude-ids="ids"
            :disabled="disabled"
            @select="addCard"
          />

          <div v-show="type === 'indexes'">
            <index-search
              @selectPlayer="addIndex"
              @selectIndex="addIndex"
            />
          </div>
        </div>

        <empty-state
          v-if="showEmptyState"
          icon="multiline_chart"
          title="Search for items to compare"
        />

        <template v-else>
          <graph
            :labels="labels"
            :datasets="datasets"
            :zoom.sync="zoom"
            :scale.sync="scale"
            :y-axis-type="yAxisType"
          />

          <compare-table
            v-if="items.length"
            :items="items"
            :zoom="zoom"
            :type="type"
            @removeItem="removeItem"
          />
        </template>
      </div>
    </template>

    <portal to="fullscreen">
      <compare-save-modal
        :open="showSaveModal"
        :editing="editing"
        :items="items"
        :type="type"
        @close="handleClose"
      />
    </portal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';

import { authService, cardService, indexService } from '@/services';
import { getCompareGraphData } from '@cardladder/shared/utils/compare.util';
import { GRAPH_COLORS, COMPARE_LIMIT } from '@cardladder/shared/constants';

import CardSearch from '@/cards/CardSearch';
import IndexSearch from '@/components/IndexSearch';
import CompareTable from './CompareTable';
import CompareSaveModal from './CompareSaveModal';

export default {
  components: {
    CardSearch,
    IndexSearch,
    CompareTable,
    CompareSaveModal,
  },

  data() {
    return {
      loading: false,
      submitting: false,
      name: '',
      editing: null,
      listener: null,
      showSaveModal: false,
      dbComparisons: [],
      labels: [],
      datasets: [],
    };
  },

  created() {
    if (this.$route.query.ids) {
      this.setCardsFromRoute();
    }
  },

  mounted() {
    this.$bus.$on('add-compare-card', (id) => this.addCard({ id }));
    this.loadSavedComparisons();
    this.setupData();
  },

  beforeDestroy() {
    this.$bus.$off('add-compare-card');
    this.listener();
  },

  watch: {
    $route(to) {
      if (to.query.ids) {
        this.setCardsFromRoute();
      }
    },

    items: {
      handler() {
        this.setupData();
      },
      deep: true,
    },

    zoom() {
      this.setupData();
    },

    type() {
      this.loadSavedComparisons();
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    zoom: {
      get() {
        const storeZoom = this.$store.state.compareGraphZoom;

        if (storeZoom && storeZoom.start && typeof storeZoom.start === 'string') {
          return {
            start: new Date(storeZoom.start),
            end: new Date(storeZoom.end),
          };
        }

        return storeZoom;
      },

      set(value) {
        this.$store.commit('SET_COMPARE_GRAPH_ZOOM', value);
      },
    },

    scale: {
      get() {
        return this.$store.state.compareGraphScale;
      },

      set(value) {
        this.$store.commit('SET_COMPARE_GRAPH_SCALE', value);
      },
    },

    type: {
      get() {
        return this.$store.state.compareType;
      },

      set(value) {
        this.$store.commit('SET_COMPARE_TYPE', value);
      },
    },

    cards: {
      get() {
        return this.$store.state.compareCards;
      },

      set(value) {
        this.$store.commit('SET_COMPARE_CARDS', value);
      },
    },

    indexes: {
      get() {
        return this.$store.state.compareIndexes;
      },

      set(value) {
        this.$store.commit('SET_COMPARE_INDEXES', value);
      },
    },

    items() {
      return this[this.type];
    },

    comparisons() {
      return this.dbComparisons
        .map((comparison) => {
          const comparisonItems = comparison.items || [];
          const comparisonIds = comparisonItems.map(({ id }) => id);

          return {
            active: this.ids.sort().join(',') === comparisonIds.sort().join(','),
            description: comparisonItems.map(({ label }) => label).join(', '),
            ...comparison,
          };
        })
        .sort((a, b) => a.name?.localeCompare(b.name));
    },

    ids() {
      return this.items.map(({ id }) => id);
    },

    disabled() {
      return this.items.length === COMPARE_LIMIT;
    },

    showSaveButton() {
      return this.comparisons.length < 10 && this.ids.length > 1;
    },

    yAxisType() {
      return this.type === 'indexes' ? 'number' : 'currency';
    },

    showEmptyState() {
      return !this.items || this.items.length === 0;
    },
  },

  methods: {
    loadSavedComparisons() {
      if (this.listener) {
        this.listener();
      }

      this.listener = firebase.firestore()
        .collection('users')
        .doc(this.user.uid)
        .collection('comparisons')
        .where('type', '==', this.type)
        .onSnapshot((querySnapshot) => {
          if (querySnapshot) {
            const dbComparisons = [];

            querySnapshot.forEach((doc) => {
              dbComparisons.push({
                ...doc.data(),
                id: doc.id,
              });
            });

            this.dbComparisons = dbComparisons;
          }
        });
    },

    setCardsFromRoute() {
      this.cards = [];

      this.$route.query.ids.split(',').forEach((id) => {
        const existingCard = this.cards.find(({ id: cardId }) => id === cardId);

        if (!existingCard) {
          this.addCard({ id });
        }
      });

      const query = { ...this.$route.query };
      delete query.ids;
      this.$router.replace({ query });
      this.type = 'cards';
    },

    async addCard(card) {
      if (this.cards.find(({ id }) => id === card.id) || this.disabled) {
        return;
      }

      this.loading = true;

      try {
        const compareCard = await cardService.getCard(card.id);
        const graphColor = this.getNextGraphColor();

        this.cards.push({
          id: compareCard.id,
          color: graphColor,
          label: compareCard.label,
          year: compareCard.year,
          player: compareCard.player,
          set: compareCard.set,
          variation: compareCard.variation,
          condition: compareCard.condition,
          number: compareCard.number,
          ebayQuery: compareCard.ebayQuery,
          pop: compareCard.pop,
          numSales: compareCard.numSales,
          marketCap: compareCard.stats ? compareCard.stats.marketCap : null,
          dailySales: compareCard.dailySales,
          ...compareCard.stats,
        });
      } catch (e) {
        this.$toasted.error('Error loading the card');
        Sentry.captureException(e);
      }

      this.loading = true;
    },

    async addIndex(index) {
      const indexId = typeof index === 'object' ? index.id : index;

      if (this.indexes.find(({ id }) => id === indexId) || this.disabled) {
        return;
      }

      this.loading = true;

      try {
        const compareIndex = await indexService.getIndex(index.player ? 'players' : 'indexes', indexId);
        const graphColor = this.getNextGraphColor();

        this.indexes.push({
          id: compareIndex.id,
          color: graphColor,
          ...compareIndex,
        });
      } catch (e) {
        this.$toasted.error('Error loading the index');
        Sentry.captureException(e);
      }

      this.loading = true;
    },

    getNextGraphColor() {
      let graphColor = GRAPH_COLORS[0];

      for (let i = 0; i < GRAPH_COLORS.length; i += 1) {
        const colorTaken = this.items.find(({ color }) => color === GRAPH_COLORS[i]);

        if (!colorTaken) {
          graphColor = GRAPH_COLORS[i];
          break;
        }
      }

      return graphColor;
    },

    setupData() {
      const { labels, datasets } = getCompareGraphData(this.items, this.zoom, this.type);

      this.labels = labels;
      this.datasets = datasets;
    },

    removeItem(itemId) {
      if (this.type === 'cards') {
        this.cards = this.cards.filter(({ id }) => id !== itemId);
      } else if (this.type === 'indexes') {
        this.indexes = this.indexes.filter(({ id }) => id !== itemId);
      }
    },

    loadComparison(comparison) {
      if (this.type === 'cards') {
        this.cards = [];
      } else if (this.type === 'indexes') {
        this.indexes = [];
      }

      comparison.items.forEach((comparison) => {
        if (this.type === 'cards') {
          this.addCard({ id: comparison.id });
        } else if (this.type === 'indexes') {
          this.addIndex(comparison);
        }
      });

      this.editing = null;
    },

    editComparison(comparison) {
      this.editing = comparison;
      this.showSaveModal = true;
    },

    saveComparison() {
      this.showSaveModal = true;
    },

    async removeComparison(comparison) {
      try {
        await authService.removeComparisons(this.user.uid, comparison.id);
        this.$toasted.success('Removed your comparison!');
      } catch (e) {
        this.$toasted.error('Error removing your comparison');
        Sentry.captureException(e);
      }
    },

    handleClose() {
      this.editing = null;
      this.showSaveModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.compare-view {
  @include container;
}

.search-container {
  margin-bottom: $gp;
  max-width: 500px;
}

.tabs {
  margin-bottom: $gp;
}
</style>
